import React, {useEffect} from 'react';

    import SimpleCardContainer from "../../ui/SimpleCardContainer";
    import {useApi} from "../../../providers/ApiProvider";
    import BusinessesModel from "../../../models/BusinessesModel";
    import {Link, useParams} from "react-router-dom";
    import {useBreadcrumbs} from "../../../providers/BreadcrumbsProvider";
    import BusinessViewHeader from "./../BusinessViewHeader";
    import BusinessViewMenu from "../BusinessViewMenu";
    import {useNotifications} from "../../../providers/NotificationsProvider";
    import {useTranslation} from "react-i18next";
    import {useTranslation as useMyTranslation} from "../../../providers/TranslationProvider";
    import ButtonWithLoading from "../../layout/ui/ButtonWithLoading";
    import BusinessCompetitorCard from "./BusinessCompetitorCard";
    import StarRating from "../../ui/StarRating";
    import CardLoading from "../../ui/CardLoading";

    export default function BusinessCompetitorsAdd({}) {
        const {language} = useMyTranslation();
        const { t} = useTranslation();
        const params = useParams();
        const notificationsContext = useNotifications();
        const apiContext = useApi();
        const businessesModel = new BusinessesModel(apiContext.api);
        const breadcrumbs = useBreadcrumbs();

        const [business, setBusiness] = React.useState(null);
        const [competitors, setCompetitors] = React.useState([]);
        const [searchResults, setSearchResults] = React.useState([]);
        const [existingCompetitors, setExistingCompetitors] = React.useState([]);
        const [loadingCompetitors, setLoadingCompetitors] = React.useState(null);
        const [loading, setLoading] = React.useState(false);
        const [searchQuery, setSearchQuery] = React.useState('');
        const [searchLoading, setSearchLoading] = React.useState(false);
        const [activeTab, setActiveTab] = React.useState('auto');


        const getExistingCompetitors = () => {
            return businessesModel.listCompetitors(params.locationId).then((response) => {
                if (response !== false) {
                    let existing = [];
                    for (let i = 0; i < response.length; i++) {
                        existing.push(response[i].id);
                    }

                    setExistingCompetitors(existing);
                }
            });
        }


        const refreshCompetitors = () => {
            setLoading(true);

            businessesModel.refreshCompetitors(params.locationId).then((response) => {
                if (response !== false) {
                    setCompetitors(response);
                }

                return getExistingCompetitors();
            }).finally(() => {
                setLoading(false);
            });
        }


        const searchCompetitorsByName = (e) => {
            if (e) e.preventDefault();
            if (!searchQuery.trim()) return;

            setSearchLoading(true);
            setSearchResults([]);

            businessesModel.searchCompetitors(params.locationId, searchQuery.trim()).then((response) => {
                if (response !== false) {
                    setSearchResults(response);
                }

                return getExistingCompetitors();
            }).finally(() => {
                setSearchLoading(false);
            });
        }


        const handleSearchKeyDown = (e) => {
            if (e.key === 'Enter') {
                searchCompetitorsByName(e);
            }
        }


        const setMetaData = (profile) => {
            if (profile !== false) {
                const title = profile.title || '';
                breadcrumbs.update({
                    title: title,
                    breadcrumbs: [
                        {label: t('Businesses List'), url: '/businesses'},
                        {label: title, url: '/businesses/'+params.locationId},
                        {label: t('Competitors'), url: '/businesses/'+params.locationId+'/competitors'},
                        {label: t('Add competitors')}
                    ]
                });

                document.title = t('Add competitors')+' | '+title+' | LocalBoost';
            }
        }


        const onProfileRefresh = (profile, lastUpdated) => {
            setMetaData(profile);
            refreshCompetitors();
        }


        const refreshData = () => {
            businessesModel.view(params.locationId).then((response) => {
                if (response !== false) {
                    setMetaData(response.data);
                    setBusiness(response.data);
                }
            });

            refreshCompetitors();
        }


        useEffect(() => {
            refreshData();
        }, [language]);


        const onAddClick = (e, competitor) => {
            e.preventDefault();
            setLoadingCompetitors(competitor.id);

            businessesModel.addCompetitor(params.locationId, competitor).then((response) => {
                if (response !== false) {
                    setMetaData(response.data);
                }
            }).finally(() => {
                setLoadingCompetitors(null);
                refreshCompetitors();
                if (activeTab === 'search') {
                    getExistingCompetitors();
                }
            });
        }


        const getCategoryName = () => {
            if (!business) return '';
            const cats = business.categories;
            if (cats?.primaryCategory?.displayName) {
                return cats.primaryCategory.displayName;
            }
            return '';
        }


        const renderCompetitorRow = (competitor, index) => {
            if (existingCompetitors.indexOf(competitor.id) !== -1) {
                return null;
            }

            return (
                <tr key={'competitor-'+competitor.id+'-'+index}>
                    <td className="">
                        <h6 className="mb-0">
                            <Link target="_blank" rel="noopener noreferrer" to={competitor.googleMapsUri}>
                                {competitor.displayName ? competitor.displayName.text : competitor.id}
                            </Link>
                        </h6>
                        <div className="d-flex gap-3 align-items-center">
                            <StarRating rating={competitor.rating} className="mb-0" />
                            <div className="fs-2 fw-medium text-muted">({competitor.userRatingCount || 0})</div>
                        </div>
                        <div className="fs-2 mt-2">{competitor.formattedAddress}</div>
                    </td>
                    <td>{competitor.primaryTypeDisplayName ? competitor.primaryTypeDisplayName.text : ''}</td>
                    <td>
                        {competitor.websiteUri ? (
                        <Link target="_blank" rel="noopener noreferrer" className="d-flex" to={competitor.websiteUri}>
                            {(() => { try { return new URL(competitor.websiteUri).hostname; } catch(e) { return competitor.websiteUri; } })()}
                        </Link>
                        ) : ''}

                        {competitor.internationalPhoneNumber ? (
                        <div className="">
                            {competitor.internationalPhoneNumber}
                        </div>
                        ) : ''}
                    </td>
                    <td style={{width: '150px'}}>
                        <div className="d-flex justify-content-end w-100">
                            <ButtonWithLoading
                                className="btn btn-success btn-sm"
                                noOriginalIconClassName={true}
                                iconClassName="d-none"
                                label={t('Add')}
                                loading={loadingCompetitors === competitor.id}
                                onClick={(e) => onAddClick(e, competitor)}
                            />
                        </div>
                    </td>
                </tr>
            );
        }


        return (
            <>
              <BusinessViewHeader
                  locationId={params.locationId}
                  onProfileRefresh={onProfileRefresh}
              />
              <BusinessViewMenu locationId={params.locationId} active="competitors" />


                <div className="my-3">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <button
                                className={"nav-link" + (activeTab === 'auto' ? ' active' : '')}
                                onClick={() => setActiveTab('auto')}
                            >
                                <i className="ti ti-map-pin me-1"></i>
                                {t('Unternehmen in der N\u00e4he')}
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={"nav-link" + (activeTab === 'search' ? ' active' : '')}
                                onClick={() => setActiveTab('search')}
                            >
                                <i className="ti ti-search me-1"></i>
                                {t('Search by name')}
                            </button>
                        </li>
                    </ul>
                </div>


                {activeTab === 'search' && (
                <>
                    <div className="card rounded-2 mb-3">
                        <div className="card-body p-4">
                            <h6 className="mb-3">{t('Search competitors by name')}</h6>
                            <div className="d-flex gap-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t('Enter business name (e.g. Worldsoft AG)')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                />
                                <ButtonWithLoading
                                    className="btn-primary"
                                    noOriginalIconClassName={true}
                                    iconClassName="d-none"
                                    label={t('Search')}
                                    loading={searchLoading}
                                    onClick={searchCompetitorsByName}
                                    disabled={!searchQuery.trim()}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={'card overflow-hidden rounded-2' + (searchLoading ? ' loading' : '')}>
                        <div className="card-body pt-3 p-4">
                            {searchResults.length > 0 ? (
                            <table className="table table-hover table-striped">
                                <thead>
                                    <tr>
                                        <th scope="col">{t('Title')}</th>
                                        <th scope="col">{t('Type')}</th>
                                        <th scope="col">{t('Contact information')}</th>
                                        <th scope="col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResults.map((competitor, index) => renderCompetitorRow(competitor, index))}
                                </tbody>
                            </table>
                            ) : (
                            <div className="text-center text-muted py-4">
                                {searchLoading ? '' : (searchQuery && searchResults.length === 0 ? t('No results found. Try a different search term.') : t('Enter a business name and click Search to find competitors.'))}
                            </div>
                            )}
                        </div>
                        <CardLoading loading={searchLoading} />
                    </div>
                </>
                )}


                {activeTab === 'auto' && (
                <>
                    <div className="d-flex justify-content-between align-items-center my-3">
                        {getCategoryName() && (
                            <span className="text-muted fs-3">
                                <i className="ti ti-category me-1"></i>
                                {t('Kategorie')}: <strong>{getCategoryName()}</strong> &middot; {t('Radius')}: <strong>25 km</strong>
                            </span>
                        )}
                        <ButtonWithLoading
                            className="btn-primary"
                            noOriginalIconClassName={true}
                            iconClassName="d-none"
                            label={t('Refresh list')}
                            loading={loading}
                            onClick={refreshCompetitors}
                        />
                    </div>

                    <div className={'card overflow-hidden rounded-2'+(loading ? ' loading' : '')}>
                        <div className="card-body pt-3 p-4">
                            {competitors.length > 0 ? (
                            <table className="table table-hover table-striped">
                                <thead>
                                    <tr>
                                        <th scope="col">{t('Title')}</th>
                                        <th scope="col">{t('Type')}</th>
                                        <th scope="col">{t('Contact information')}</th>
                                        <th scope="col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {competitors.map((competitor, index) => renderCompetitorRow(competitor, index))}
                                </tbody>
                            </table>
                            ) : (
                            <div className="text-center text-muted py-4">
                                {loading ? '' : t('Keine Unternehmen in der N\u00e4he gefunden. Versuchen Sie die Liste zu aktualisieren.')}
                            </div>
                            )}
                        </div>
                        <CardLoading loading={loading} />
                    </div>
                </>
                )}

                <div className="mt-3">
                    <Link to={'/businesses/'+params.locationId+'/competitors'} className="btn btn-outline-secondary">
                        <i className="ti ti-arrow-left me-1"></i>
                        {t('Back to all competitors')}
                    </Link>
                </div>
            </>
        );
    }
  